/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Button, Drawer, Form, Input, message, Spin, Typography, Grid, Switch, Divider } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCreateCustomer } from '../Pages/customers/Hooks';
import { validations } from '../utils/validation';
import PropTypes from 'prop-types';
import { useOperatorStore } from '../store/useOperatorStore';
const { Title } = Typography;
const { useBreakpoint } = Grid;
const CustomerDrawer = ({ open, onClose, tagId }) => {
  const { operatorId } = useOperatorStore();
  const { t } = useTranslation('CustomerView');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { mutate: createCustomer, isPending } = useCreateCustomer({
    onSuccess: () => {
      message.success(t('CustomerCreated'));
      queryClient.invalidateQueries(['users']);
      form.resetFields();
      onClose();
      setLoading(false);
    },
    onError: (error) => {
      message.error(
        error.response?.data?.message || t('CustomerCreationFailed')
      );
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    setLoading(true);

    // Remove email if it's empty or undefined
    if (!values.email) {
      delete values.email;
    }

    const payload = {
      ...values,
      operatorId,
      startChargeEnabled: values.startChargeEnabled !== false,
    };

    createCustomer(payload);
  };

  return (
    <Drawer
      title={<Title level={4} className="!mb-0">{t('CreateNewCustomer')}</Title>}
      width={isMobile ? '100%' : 600}
      maskClosable={false}
      onClose={onClose}
      open={open}
      destroyOnHidden
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            countryCode: import.meta.env.VITE_CURRENCY === 'TRY' ? '90' : '91',
            tagId: tagId || '',
            startChargeEnabled: true,
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="fname"
              label={t('FirstName')}
              rules={[
                validations.required(t('FirstNameRequired')),
                validations.maxLength(20, t('Max20Characters')),
              ]}
            >
              <Input placeholder={t('EnterFirstName')} />
            </Form.Item>

            <Form.Item
              name="lname"
              label={t('LastName')}
              rules={[
                validations.required(t('LastNameRequired')),
                validations.maxLength(20, t('Max20Characters')),
              ]}
            >
              <Input placeholder={t('EnterLastName')} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="phoneNumber"
              label={t('PhoneNumber')}
              rules={[
                validations.required(t('PhoneNumberRequired')),
                validations.phoneNumber(t('InvalidPhoneNumber')),
                validations.noSpaces(t('NoWhitespaceAllowed')),
              ]}
            >
              <Input
                placeholder={t('EnterPhoneNumber')}
                prefix={'+90'}
              />
            </Form.Item>
            <Form.Item
              name="email"
              label={t('Email')}
              rules={[validations.email(t('InvalidEmail'))]}
            >
              <Input placeholder={t('EnterEmail')} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="employeeId"
              label={t('EmployeeId')}
              rules={[
                validations.required(t('EmployeeIdRequired')),
                validations.noSpaces(t('NoWhitespaceAllowed')),
              ]}
            >
              <Input placeholder={t('EnterEmployeeId')} />
            </Form.Item>
            <Form.Item
              name="factoryNumber"
              label={t('FactoryNumber')}
              rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
            >
              <Input placeholder={t('EnterFactoryNumber')} />
            </Form.Item>
          </div>

          <Form.Item
            name="tagId"
            label={t('TagId')}
          >
            <Input placeholder={t('TagId')} />
          </Form.Item>

          <Form.Item
            name={['address', 'line1']}
            label={t('AddressLine1')}
            rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
          >
            <Input placeholder={t('EnterAddressLine1')} />
          </Form.Item>

          <Form.Item
            name={['address', 'line2']}
            label={t('AddressLine2')}
            rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
          >
            <Input placeholder={t('EnterAddressLine2')} />
          </Form.Item>

          <Divider orientation="left">{t('StartCharge')}</Divider>
          <Form.Item
            name="startChargeEnabled"
            label={t('StartChargeEnabled')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="countryCode" hidden>
            <Input type="hidden" />
          </Form.Item>

          <div className="flex justify-start gap-2 mt-4">
            <Button type="primary" htmlType="submit" loading={isPending}>
              {t('CreateCustomer')}
            </Button>
            <Button onClick={onClose}>{t('Cancel')}</Button>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

CustomerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tagId: PropTypes.string,
};

export default CustomerDrawer;
